if (!customElements.get('localization-banner')) {
  const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  const CONSENT_TIMEOUT = 800;

  // Country names are rendered and sorted in English so the list reads the same in every
  // market — unrelated to which language the banner pre-selects.
  const LIST_LOCALE = 'en';

  // Pre-consent defaults. The primary audience is French, so a visitor who has not been
  // placed in a market by the URL sees France + French already selected and can accept
  // without hunting through either switcher. Overridable per instance with
  // data-default-country / data-default-language.
  const DEFAULT_COUNTRY = 'FR';
  const DEFAULT_LANGUAGE = 'fr';

  const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';
  const KEYBOARD_INSET_MIN = 80;

  const ENGLISH_REGIONS = (function () {
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' });
    } catch (error) {
      return null;
    }
  })();

  function englishCountryName(code, fallback) {
    const wanted = String(code || '').toUpperCase();
    if (!ENGLISH_REGIONS || !/^[A-Z]{2}$/.test(wanted)) return fallback;

    try {
      const name = ENGLISH_REGIONS.of(wanted);
      return !name || name === wanted ? fallback : name;
    } catch (error) {
      return fallback;
    }
  }

  customElements.define(
    'localization-banner',
    class LocalizationBanner extends HTMLElement {
      connectedCallback() {
        this.gate = this.querySelector('[data-cookie-gate]');
        this.form = this.querySelector('[data-gate-form]');
        this.payloadNode = this.querySelector('[data-gate-payload]');

        if (!this.gate) return;

        this.consentKey = this.dataset.consentKey || 'ml_cookie_gate_v4';
        this.preferenceKey = this.dataset.preferenceKey || 'ml_market_pref_v4';
        this.expanded = null;
        this.confirming = false;
        this.teardown = [];
        this.motion = new WeakMap();
        this.viewportWatch = null;

        if (this.isDesignMode() || this.hasPreference()) return;

        try {
          this.parsePayload();
        } catch (error) {
          this.failOpen();
          return;
        }

        this.current.languageCode = this.resolveCurrentLanguage();

        this.selectedCountry = this.defaultCountry();
        this.selectedLanguage = this.defaultLanguage();
        this.errorNode = this.querySelector('[data-gate-error]');

        this.buildCountrySelect();
        this.buildLanguageSelect();
        this.validate();
        this.bindActions();
        this.reveal();
      }

      disconnectedCallback() {
        this.unwatchViewport();

        this.teardown.forEach(function (off) {
          off();
        });
        this.teardown = [];

        [this.countryEntry, this.languageEntry].forEach((entry) => {
          if (entry) entry.rows.forEach((row) => this.settle(row));
        });
      }

      /* ---------- state ---------- */

      isDesignMode() {
        return !!(window.Shopify && window.Shopify.designMode);
      }

      hasPreference() {
        try {
          return !!window.localStorage.getItem(this.preferenceKey);
        } catch (error) {
          return false;
        }
      }

      parsePayload() {
        const payload = JSON.parse(this.payloadNode.textContent);

        this.countries = (payload.countries || []).map(function (entry) {
          return {
            code: entry.c,
            name: englishCountryName(entry.c, entry.n),
            popular: entry.p,
            currencyCode: entry.u,
            currencySymbol: entry.s,
            market: entry.m,
            languageCodes: entry.l || [],
          };
        });

        this.languages = (payload.languages || []).map(function (entry) {
          return {
            code: entry.c,
            name: entry.n,
            root: entry.r,
          };
        });

        this.current = {
          countryCode: payload.current.country,
          languageCode: payload.current.language,
          rootUrl: payload.current.root_url,
        };

        if (!this.countries.length) throw new Error('localization payload has no countries');
        if (!this.languages.length) throw new Error('localization payload has no languages');
      }

      findCountry(code) {
        const wanted = String(code || '').toUpperCase();
        return this.countries.find(function (country) {
          return country.code === wanted;
        });
      }

      // The country Shopify is already serving wins: it is the market the URL put the visitor
      // in, so an ad landing page or a shared /en-us link keeps its own pricing. Only when
      // that country is not in the list does the configured default apply.
      defaultCountry() {
        return (
          this.findCountry(this.current.countryCode) ||
          this.findCountry(this.dataset.defaultCountry || DEFAULT_COUNTRY) ||
          this.countries[0]
        );
      }

      // Same rule for language, plus a support check so the pair is never invalid on first
      // paint. On the bare primary root the current language is the primary market's own
      // default (French), which is exactly what we want pre-selected.
      defaultLanguage() {
        const candidates = [this.current.languageCode, this.dataset.defaultLanguage || DEFAULT_LANGUAGE];

        for (let i = 0; i < candidates.length; i++) {
          const language = this.findLanguage(candidates[i]);
          if (language && this.countrySupports(this.selectedCountry, language)) return language;
        }

        return this.findLanguage(this.current.languageCode) || this.languages[0];
      }

      countrySupports(country, language) {
        const codes = country.languageCodes;
        if (!codes || !codes.length) return true;

        return codes.indexOf(language.code) !== -1;
      }

      findLanguage(code) {
        const wanted = String(code || '');
        return this.languages.find(function (language) {
          return language.code === wanted;
        });
      }

      trimRoot(root) {
        return !root || root === '/' ? '' : root.replace(/\/+$/, '');
      }

      matchesRoot(path, root) {
        const target = this.trimRoot(root);
        if (!target) return false;

        const rest = path.slice(target.length);
        return path.startsWith(target) && (rest === '' || rest.charAt(0) === '/');
      }

      resolveCurrentLanguage() {
        const path = window.location.pathname;

        const active = this.languages
          .filter((language) => this.matchesRoot(path, language.root))
          .sort((a, b) => this.trimRoot(b.root).length - this.trimRoot(a.root).length)[0];

        return active ? active.code : this.current.languageCode;
      }

      /* ---------- rendering ---------- */

      countryLabel(country) {
        return country.currencyCode ? country.name + ' (' + country.currencyCode + ')' : country.name;
      }

      countryOptions() {
        const byName = function (a, b) {
          return a.name.localeCompare(b.name, LIST_LOCALE);
        };
        const toOption = (country) => {
          return { value: country.code, label: this.countryLabel(country) };
        };

        const popular = this.countries
          .filter(function (country) {
            return country.popular;
          })
          .sort(byName);

        const rest = this.countries
          .filter(function (country) {
            return !country.popular;
          })
          .sort(byName);

        if (!popular.length || !rest.length) {
          return popular.concat(rest).map(toOption);
        }

        return [{ group: this.dataset.labelPopular }]
          .concat(popular.map(toOption))
          .concat([{ group: this.dataset.labelAll }])
          .concat(rest.map(toOption));
      }

      languageOptions() {
        return this.languages.map(function (language) {
          return { value: language.code, label: language.name };
        });
      }

      buildCountrySelect() {
        const select = this.querySelector('[data-gate-select="country"]');
        if (!select) return;

        this.renderOptions(select, this.countryOptions());
        this.countryEntry = this.bindSelect(select, (value) => this.onCountryChange(value));
        this.syncSelection(this.countryEntry, this.selectedCountry.code, this.countryLabel(this.selectedCountry));
      }

      buildLanguageSelect() {
        const select = this.querySelector('[data-gate-select="language"]');
        if (!select) return;

        this.renderOptions(select, this.languageOptions());
        this.languageEntry = this.bindSelect(select, (value) => this.onLanguageChange(value));
        this.syncSelection(this.languageEntry, this.selectedLanguage.code, this.selectedLanguage.name);
      }

      renderOptions(select, options) {
        const list = select.querySelector('[data-gate-list]');

        list.innerHTML = '';

        options.forEach(function (option) {
          const item = document.createElement('li');

          if (option.group) {
            item.className = 'cookie-select__group';
            item.setAttribute('role', 'presentation');
            item.dataset.gateGroup = '';
            item.textContent = option.group;
            list.appendChild(item);
            return;
          }

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cookie-select__option';
          button.textContent = option.label;
          button.dataset.gateOption = option.value;
          button.setAttribute('role', 'option');
          button.setAttribute('aria-selected', 'false');

          item.className = 'cookie-select__item';
          item.setAttribute('role', 'presentation');
          item.dataset.gateRow = '';
          item.appendChild(button);
          list.appendChild(item);
        });
      }

      syncSelection(entry, value, label) {
        if (!entry) return;

        entry.list.querySelectorAll('[data-gate-option]').forEach(function (option) {
          option.setAttribute('aria-selected', option.dataset.gateOption === value ? 'true' : 'false');
        });

        entry.select.querySelector('[data-gate-value]').textContent = label;
      }

      /* ---------- selection ---------- */

      onCountryChange(code) {
        const country = this.findCountry(code);
        if (!country || country.code === this.selectedCountry.code) return;

        this.selectedCountry = country;

        this.syncSelection(this.countryEntry, country.code, this.countryLabel(country));
        this.validate();
      }

      onLanguageChange(code) {
        const language = this.findLanguage(code);
        if (!language || language.code === this.selectedLanguage.code) return;

        this.selectedLanguage = language;

        this.syncSelection(this.languageEntry, language.code, language.name);
        this.validate();
      }

      /* ---------- validation ---------- */

      isSupported() {
        return this.countrySupports(this.selectedCountry, this.selectedLanguage);
      }

      validate() {
        const supported = this.isSupported();
        const entry = this.languageEntry;

        if (entry) {
          entry.select.classList.toggle('is-invalid', !supported);
          entry.toggle.setAttribute('aria-invalid', supported ? 'false' : 'true');
        }

        if (this.errorNode) {
          this.errorNode.textContent = supported ? '' : this.dataset.labelUnavailable || '';
          this.errorNode.hidden = supported;
        }

        return supported;
      }

      /* ---------- dropdown behaviour ---------- */

      bindSelect(select, onChange) {
        const toggle = select.querySelector('[data-gate-toggle]');
        const panel = select.querySelector('[data-gate-panel]');
        const list = select.querySelector('[data-gate-list]');
        const filter = select.querySelector('[data-gate-filter]');
        const empty = select.querySelector('[data-gate-empty]');
        const rows = Array.from(list.querySelectorAll('[data-gate-row]'));
        const entry = { select, toggle, panel, list, filter, empty, rows };

        const onToggleClick = () => {
          this.isOpen(entry) ? this.closeSelect(entry) : this.openSelect(entry);
        };

        const onPanelClick = (event) => {
          const option = event.target.closest('[data-gate-option]');
          if (!option) return;

          this.closeSelect(entry);
          onChange(option.dataset.gateOption);
          toggle.focus();
        };

        const onKeydown = (event) => {
          if (event.key === 'Escape' && this.isOpen(entry)) {
            event.stopPropagation();
            this.closeSelect(entry);
            toggle.focus();
            return;
          }

          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!this.isOpen(entry)) this.openSelect(entry);
            this.moveFocus(entry, event.key === 'ArrowDown' ? 1 : -1);
          }
        };

        const onFilterInput = () => this.applyFilter(entry);

        const onFilterKeydown = (event) => {
          if (event.key !== 'Enter') return;

          event.preventDefault();

          const first = this.visibleOptions(entry)[0];
          if (!first) return;

          this.closeSelect(entry);
          onChange(first.dataset.gateOption);
          toggle.focus();
        };

        toggle.addEventListener('click', onToggleClick);
        panel.addEventListener('click', onPanelClick);
        select.addEventListener('keydown', onKeydown);
        if (filter) filter.addEventListener('input', onFilterInput);
        if (filter) filter.addEventListener('keydown', onFilterKeydown);

        this.teardown.push(function () {
          toggle.removeEventListener('click', onToggleClick);
          panel.removeEventListener('click', onPanelClick);
          select.removeEventListener('keydown', onKeydown);
          if (filter) filter.removeEventListener('input', onFilterInput);
          if (filter) filter.removeEventListener('keydown', onFilterKeydown);
        });

        return entry;
      }

      isOpen(entry) {
        return this.expanded === entry;
      }

      openSelect(entry) {
        if (this.expanded && this.expanded !== entry) this.closeSelect(this.expanded);

        if (entry.filter) {
          entry.filter.value = '';
          this.applyFilter(entry, true);
        }

        entry.panel.classList.add('is-open');
        entry.toggle.setAttribute('aria-expanded', 'true');
        this.expanded = entry;

        if (entry.filter) {
          this.watchViewport(entry);
          if (!this.isTouchLayout()) entry.filter.focus({ preventScroll: true });
        }

        const selected = entry.list.querySelector('[aria-selected="true"]');
        if (selected) selected.scrollIntoView({ block: 'nearest' });
      }

      closeSelect(entry) {
        if (entry.filter) entry.filter.blur();

        this.unwatchViewport();

        entry.panel.classList.remove('is-open');
        entry.toggle.setAttribute('aria-expanded', 'false');

        if (this.expanded === entry) this.expanded = null;
      }

      /* ---------- keyboard docking ---------- */

      isTouchLayout() {
        return window.matchMedia(TOUCH_QUERY).matches;
      }

      watchViewport(entry) {
        const viewport = window.visualViewport;
        if (!viewport) return;

        this.unwatchViewport();

        const sync = () => this.syncViewport(entry);

        viewport.addEventListener('resize', sync);
        viewport.addEventListener('scroll', sync);

        this.viewportWatch = () => {
          viewport.removeEventListener('resize', sync);
          viewport.removeEventListener('scroll', sync);
          entry.select.classList.remove('is-docked');
        };

        sync();
      }

      unwatchViewport() {
        if (this.viewportWatch) {
          this.viewportWatch();
          this.viewportWatch = null;
        }

        if (!this.gate) return;

        this.gate.style.removeProperty('--cookie-gate-kb-inset');
        this.gate.style.removeProperty('--cookie-gate-vv-height');
      }

      syncViewport(entry) {
        const viewport = window.visualViewport;
        if (!viewport) return;

        const inset = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));

        this.gate.style.setProperty('--cookie-gate-kb-inset', inset + 'px');
        this.gate.style.setProperty('--cookie-gate-vv-height', Math.round(viewport.height) + 'px');

        entry.select.classList.toggle('is-docked', this.isTouchLayout() && inset >= KEYBOARD_INSET_MIN);
      }

      visibleOptions(entry) {
        return entry.rows
          .filter((row) => !row.hidden && !row.classList.contains('is-leaving'))
          .map((row) => row.querySelector('[data-gate-option]'))
          .filter(Boolean);
      }

      moveFocus(entry, direction) {
        const options = this.visibleOptions(entry);
        if (!options.length) return;

        const active = options.indexOf(document.activeElement);

        if (active === -1) {
          const selected = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
          options[selected === -1 ? 0 : selected].focus();
          return;
        }

        options[Math.min(Math.max(active + direction, 0), options.length - 1)].focus();
      }

      /* ---------- filtering ---------- */

      reduceMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      canAnimate(entry) {
        return this.isOpen(entry) && !this.reduceMotion() && typeof entry.panel.animate === 'function';
      }

      play(element, keyframes, timing) {
        this.stop(element);

        const animation = element.animate(keyframes, Object.assign({ easing: EASE }, timing));
        this.motion.set(element, animation);

        return animation;
      }

      stop(element) {
        const animation = this.motion.get(element);
        if (!animation) return;

        animation.cancel();
        this.motion.delete(element);
      }

      settle(row) {
        this.stop(row);

        if (!row.classList.contains('is-leaving')) return;

        row.classList.remove('is-leaving');
        row.style.top = '';
      }

      rowMatches(row, term) {
        return !term || row.textContent.toLowerCase().indexOf(term) !== -1;
      }

      applyFilter(entry, immediate) {
        if (!entry.filter) return;

        const term = entry.filter.value.trim().toLowerCase();

        if (immediate || !this.canAnimate(entry)) {
          entry.rows.forEach((row) => {
            this.settle(row);
            row.hidden = !this.rowMatches(row, term);
          });

          this.syncGroups(entry);
          this.syncEmpty(entry);
          return;
        }

        const origin = entry.list.getBoundingClientRect().top + entry.list.clientTop - entry.list.scrollTop;
        const panelHeight = entry.panel.offsetHeight;
        const before = new Map();

        entry.rows.forEach((row) => {
          if (row.hidden || row.classList.contains('is-leaving')) return;
          before.set(row, row.getBoundingClientRect().top);
        });

        const leaving = [];

        entry.rows.forEach((row) => {
          if (this.rowMatches(row, term)) {
            this.settle(row);
            row.hidden = false;
            return;
          }

          if (!before.has(row)) {
            this.settle(row);
            row.hidden = true;
            return;
          }

          leaving.push([row, before.get(row) - origin]);
        });

        leaving.forEach(([row, offset]) => this.leave(entry, row, offset));

        this.syncGroups(entry);
        this.syncEmpty(entry);
        this.shiftInto(entry, before, panelHeight);
      }

      leave(entry, row, offset) {
        row.classList.add('is-leaving');
        row.style.top = offset + 'px';

        const animation = this.play(
          row,
          [
            { opacity: 1, transform: 'translateY(0)' },
            { opacity: 0, transform: 'translateY(-4px)' },
          ],
          { duration: 150, fill: 'forwards' }
        );

        animation.finished
          .then(() => {
            if (this.motion.get(row) !== animation) return;

            this.settle(row);
            row.hidden = true;

            this.syncGroups(entry);
            this.syncEmpty(entry);
          })
          .catch(() => {});
      }

      shiftInto(entry, before, panelHeight) {
        this.stop(entry.panel);

        const bounds = entry.list.getBoundingClientRect();
        const height = entry.panel.offsetHeight;

        if (Math.abs(height - panelHeight) > 0.5) {
          this.play(entry.panel, [{ height: panelHeight + 'px' }, { height: height + 'px' }], { duration: 260 });
        }

        entry.rows.forEach((row) => {
          if (row.hidden || row.classList.contains('is-leaving')) return;

          const rect = row.getBoundingClientRect();
          if (rect.bottom < bounds.top - 40 || rect.top > bounds.bottom + 40) {
            this.stop(row);
            return;
          }

          const previous = before.get(row);

          if (previous === undefined) {
            this.play(row, [{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], {
              duration: 180,
            });
            return;
          }

          const delta = previous - rect.top;
          if (Math.abs(delta) < 0.5) return;

          this.play(row, [{ transform: 'translateY(' + delta + 'px)' }, { transform: 'translateY(0)' }], {
            duration: 260,
          });
        });
      }

      syncGroups(entry) {
        entry.list.querySelectorAll('[data-gate-group]').forEach(function (group) {
          let sibling = group.nextElementSibling;
          let visible = false;

          while (sibling && !sibling.hasAttribute('data-gate-group')) {
            if (!sibling.hidden) visible = true;
            sibling = sibling.nextElementSibling;
          }

          group.hidden = !visible;
        });
      }

      syncEmpty(entry) {
        if (!entry.empty) return;

        entry.empty.hidden = entry.rows.some(function (row) {
          return !row.hidden;
        });
      }

      /* ---------- confirm ---------- */

      bindActions() {
        const accept = this.querySelector('[data-cookie-gate-accept]');
        const decline = this.querySelector('[data-cookie-gate-decline]');

        const onAccept = () => this.confirm(true);
        const onDecline = () => this.confirm(false);
        const onDocumentClick = (event) => {
          if (this.expanded && !this.expanded.select.contains(event.target)) this.closeSelect(this.expanded);
        };

        if (accept) accept.addEventListener('click', onAccept);
        if (decline) decline.addEventListener('click', onDecline);
        document.addEventListener('click', onDocumentClick);

        this.teardown.push(function () {
          if (accept) accept.removeEventListener('click', onAccept);
          if (decline) decline.removeEventListener('click', onDecline);
          document.removeEventListener('click', onDocumentClick);
        });
      }

      confirm(accepted) {
        if (this.confirming) return;

        if (!this.validate()) {
          if (this.languageEntry) this.languageEntry.toggle.focus();
          return;
        }

        this.confirming = true;

        this.write(this.consentKey, accepted ? '1' : '0');
        this.write(
          this.preferenceKey,
          JSON.stringify({
            country: this.selectedCountry.code,
            currency: this.selectedCountry.currencyCode,
            language: this.selectedLanguage.code,
            market: this.selectedCountry.market,
          })
        );

        this.applyConsent(accepted, () => this.submit());
      }

      submit() {
        if (!this.form) {
          this.dismiss();
          return;
        }

        this.form.querySelector('[data-gate-country]').value = this.selectedCountry.code;
        this.form.querySelector('[data-gate-language]').value = this.selectedLanguage.code;
        this.form.querySelector('[data-gate-return-to]').value = this.buildPath(this.selectedLanguage.root);
        this.form.submit();
      }

      strippedPath() {
        const path = window.location.pathname;

        const matched = this.languages
          .map((language) => language.root)
          .concat([this.current.rootUrl])
          .map((root) => this.trimRoot(root))
          .filter((root) => this.matchesRoot(path, root))
          .sort((a, b) => b.length - a.length)[0];

        return matched ? path.slice(matched.length) : path;
      }

      buildPath(targetRoot) {
        const target = this.trimRoot(targetRoot);
        const path = this.strippedPath();

        return target + (path || '/') + window.location.search || '/';
      }

      applyConsent(accepted, done) {
        let settled = false;

        const settle = () => {
          if (settled) return;
          settled = true;
          if (typeof done === 'function') done();
        };

        if (accepted && typeof window.consentGrantedAdStorage === 'function') {
          window.consentGrantedAdStorage();
        }

        if (!(window.Shopify && window.Shopify.loadFeatures)) {
          settle();
          return;
        }

        setTimeout(settle, CONSENT_TIMEOUT);

        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
          if (error || !window.Shopify.customerPrivacy) {
            settle();
            return;
          }

          window.Shopify.customerPrivacy.setTrackingConsent(accepted, settle);
        });
      }

      write(key, value) {
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          /* private mode — the choice still applies to this page load */
        }
      }

      /* ---------- visibility ---------- */

      reveal() {
        this.gate.classList.add('is-visible');
        document.documentElement.classList.add('cookie-gate--active');
      }

      dismiss() {
        document.documentElement.classList.remove('cookie-gate--active');
        this.gate.classList.add('is-leaving');

        setTimeout(() => {
          if (this.gate && this.gate.parentNode) this.gate.parentNode.removeChild(this.gate);
        }, 400);
      }

      failOpen() {
        document.documentElement.classList.remove('cookie-gate--active');
        if (this.gate && this.gate.parentNode) this.gate.parentNode.removeChild(this.gate);
      }
    }
  );
}
